import { bool, number } from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";
import { SMALL_POPOVER_WIDTH } from "theme/constants";

import FnrTypography from "FnrTypography";
import { useSetHousholdName } from "HouseholdProfile/API/queries";
import {
  useHouseholdData,
  useIsSingleUserHousehold,
} from "HouseholdProfile/API/queryHooks";
import { HouseholdProfileProvider } from "HouseholdProfile/HouseholdProfile";
import { useCanEditHouseholdTitle } from "UserProfile/API/queryHooks";
import { AnimatePresence, motion } from "framer-motion";

import fnrIconButtonProps from "UI/FnrIconButton/fnrIconButtonProps";
import FnrPencilEditInput from "UI/FnrPencilEditInput";
import FnrTypeNSave from "UI/FnrTypeNSave";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";
import { inOutZoom } from "UI/motionStyles";

import useWidth from "Utilities/Hooks/useWidth";
import truncate from "Utilities/truncate";

const NameWrapper = styled("div")`
  ${({ $containerWidth }) => `width: calc(${$containerWidth}px - 48px);`}
`;

const Container = styled("div")`
  min-height: 52px;
`;

const SMotionDiv = styled(motion.div)`
  display: inline-flex;
`;

const SFnrTypeNSave = styled(FnrTypeNSave)`
  min-width: ${SMALL_POPOVER_WIDTH}px;
`;
SFnrTypeNSave.name = "FnrTypeNSave"; // FnrPencilEditInput uses this to know how to save

const EditButtonMeasurer = styled("div")`
  display: inline;
`;

const HouseholdNameInputContents = ({ isDark }) => {
  const { name: householdName, householdID, isLoading } = useHouseholdData();
  const isSingleUserHousehold = useIsSingleUserHousehold();
  const [value, setValue] = useState(householdName || "");
  const { mutateAsync, isLoading: isSettingName } = useSetHousholdName();
  const onSave = useCallback(async () => {
    await mutateAsync(value);
  }, [mutateAsync, value]);
  const hasHouseholdName = truncate(householdName);

  useEffect(() => {
    // anytime the api updates this value, it needs to sync with the input
    setValue(householdName || "");
  }, [householdName]);

  const typographyProps = {
    color: isDark ? "textLight" : "textBlack",
    fontSize: "xxxl",
    fontWeight: 300,
  };

  const containerRef = useRef();
  const containerWidth = useWidth(containerRef);
  const nameWrapperRef = useRef();
  const editButtonMeasureRef = useRef();
  const textWidth = useWidth(editButtonMeasureRef);
  const canEditHouseholdTitle = useCanEditHouseholdTitle();
  const editButtonXAdjust = useMemo(() => {
    return -(containerWidth - textWidth) + 48;
  }, [containerWidth, textWidth]);

  return (
    <Container ref={containerRef} gap={0}>
      <FnrVerticalGap gap={0}>
        <AnimatePresence initial={false}>
          {!isLoading && (
            <SMotionDiv key={householdID} {...inOutZoom}>
              <FnrPencilEditInput
                labelSuffix="household-name"
                Input={SFnrTypeNSave}
                onSave={onSave}
                passOnSaveTo="onSubmit"
                iconButtonProps={fnrIconButtonProps.legacy}
                editButtonXAdjust={editButtonXAdjust}
                disabled={isSingleUserHousehold || !canEditHouseholdTitle}
                inputProps={{
                  autoFocus: true,
                  placeholder: "Family Name",
                  buttonAriaLabel: "save-household-name",
                  value,
                  setValue,
                  disabled: value === householdName,
                  isLoading: isSettingName,
                }}
              >
                <NameWrapper
                  ref={nameWrapperRef}
                  $containerWidth={containerWidth}
                >
                  <EditButtonMeasurer ref={editButtonMeasureRef}>
                    {hasHouseholdName && (
                      <>
                        {!isSingleUserHousehold && (
                          <FnrTypography {...typographyProps}>
                            The&nbsp;
                          </FnrTypography>
                        )}
                        <FnrTypography {...typographyProps} fontWeight={400}>
                          {householdName}
                        </FnrTypography>
                      </>
                    )}
                    {!isSingleUserHousehold && (
                      <FnrTypography {...typographyProps}>
                        {hasHouseholdName && <>&nbsp;</>}Family
                      </FnrTypography>
                    )}
                  </EditButtonMeasurer>
                </NameWrapper>
              </FnrPencilEditInput>
            </SMotionDiv>
          )}
        </AnimatePresence>
      </FnrVerticalGap>
    </Container>
  );
};

HouseholdNameInputContents.propTypes = { isDark: bool };

const HouseholdNameInput = ({ householdID, isDark }) => (
  <HouseholdProfileProvider householdID={householdID}>
    <HouseholdNameInputContents isDark={isDark} />
  </HouseholdProfileProvider>
);

HouseholdNameInput.propTypes = { householdID: number, isDark: bool };

export default HouseholdNameInput;
